import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";

import CartItem from "../../components/home/cartComponents/CartItem";
import EmptyCart from "./cartComponents/EmptyCart";
import SectionHeader from "../../components/home/membershipPlanComponents/SectionHeader";
import { fetchCart, removeFromCartAsync } from "../../features/cartSlice";
import { fetchCurrentSubscription } from "../../features/subscriptionSlice";
import { fetchUserOrders } from "../../features/orderSlice";

export default function Cart() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector((state) => state.auth.user?.userId);
  const { items = [], status, error } = useSelector((state) => state.cart);

  const [selectedBooks, setSelectedBooks] = useState([]);
  const [limitExceededCount, setLimitExceededCount] = useState(null);
  const [showLimitExceededModal, setShowLimitExceededModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  useEffect(() => {
    if (items && items.length > 0) {
      const availableBooks = items
        .filter((item) => item.book?.bookStockStatus !== "OUT_OF_STOCK")
        .map((item) => item.book?.bookId)
        .filter(Boolean);
      setSelectedBooks(availableBooks);
    }
  }, [items]);

  useEffect(() => {
    if (userId) {
      dispatch(fetchCart(userId));
    }
  }, [userId, dispatch]);

  const handleRemove = (bookId) => {
    dispatch(removeFromCartAsync({ userId, bookId }));
  };

  const handleSelect = (book) => {
    if (book.bookStockStatus === "OUT_OF_STOCK") {
      toast.error("This book is currently out of stock.");
      return;
    }

    const bookId = book.bookId;
    setSelectedBooks((prev) =>
      prev.includes(bookId)
        ? prev.filter((id) => id !== bookId)
        : [...prev, bookId]
    );
  };

  const availableBooks = (items || [])
    .filter((item) => item.book?.bookStockStatus !== "OUT_OF_STOCK")
    .map((item) => item.book?.bookId)
    .filter(Boolean);

  const isAllSelected =
    availableBooks.length > 0 &&
    availableBooks.every((id) => selectedBooks.includes(id));

  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedBooks([]);
    } else {
      setSelectedBooks(availableBooks);
    }
  };

  const handleProceedToRent = async () => {
    if (selectedBooks.length === 0) {
      toast.error("Please select books to rent.");
      return;
    }

    if (!userId) {
      toast.error("Please log in to proceed.");
      navigate("/login");
      return;
    }

    try {
      const subscriptionResult = await dispatch(
        fetchCurrentSubscription(userId)
      ).unwrap();
      const subscription = subscriptionResult?.subscription;

      if (!subscription || subscription.status !== "ACTIVE") {
        setShowSubscriptionModal(true);
        return;
      }

      if (
        subscription.remainingDays !== undefined &&
        subscription.remainingDays <= 0
      ) {
        toast.error("Your subscription has expired.");
        navigate("/membershipPlans");
        return;
      }

      const packageType = subscription.packageType;
      const allowedBookLimit =
        {
          ONE_BOOK: 1,
          TWO_BOOKS: 2,
          THREE_BOOKS: 3,
        }[packageType] || 0;

      const userOrdersResult = await dispatch(fetchUserOrders(userId)).unwrap();
      const activeOrdersCount = (userOrdersResult || []).filter(
        (o) => o?.returnStatus === "NOT_RETURNED"
      ).length;

      const remainingLimit = allowedBookLimit - activeOrdersCount;

      if (selectedBooks.length > remainingLimit) {
        setLimitExceededCount(remainingLimit);
        setShowLimitExceededModal(true);
        return;
      }

      const queryParam = selectedBooks.join(",");
      navigate(`/multi-book-order?bookIds=${queryParam}`);
    } catch (err) {
      console.error("Failed during proceed to rent check:", err);
      toast.error("Failed to verify subscription or order limit.");
    }
  };

  if (status === "loading") {
    return (
      <div className="w-full min-h-[60vh]">
        <SectionHeader title="My Cart" />
        <div className="max-w-6xl mx-auto px-4 py-8 space-y-4">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="animate-pulse bg-white rounded-xl border border-gray-200 p-5 flex gap-4"
            >
              <div className="w-20 h-28 bg-gray-200 rounded-lg flex-shrink-0" />
              <div className="flex-1 space-y-2.5">
                <div className="h-5 bg-gray-200 rounded w-1/2" />
                <div className="h-4 bg-gray-200 rounded w-1/3" />
                <div className="h-4 bg-gray-200 rounded w-1/4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="w-full min-h-[60vh]">
        <SectionHeader title="My Cart" />
        <div className="max-w-md mx-auto px-4 py-12 text-center">
          <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-6 rounded-xl space-y-3">
            <p className="font-medium">{error || "Failed to load cart items"}</p>
            {userId && (
              <button
                onClick={() => dispatch(fetchCart(userId))}
                className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary-shade4 transition-colors"
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="w-full min-h-[70vh] pb-16">
      {/* Page Header */}
      <div className="w-full">
        <SectionHeader title="My Cart" />
      </div>

      {!items || items.length === 0 ? (
        <div className="mt-6">
          <EmptyCart />
        </div>
      ) : (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Top Control Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 pb-4 mb-6 border-b border-gray-200/80">
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2.5 cursor-pointer select-none text-sm font-medium text-gray-700 hover:text-primary transition-colors">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={handleToggleSelectAll}
                  disabled={availableBooks.length === 0}
                  className="sr-only"
                />
                <div
                  className={`w-5 h-5 rounded-md flex items-center justify-center border transition-all duration-150 ${
                    isAllSelected
                      ? "bg-primary border-primary shadow-sm"
                      : "bg-white border-gray-300 hover:border-primary"
                  } ${availableBooks.length === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {isAllSelected && (
                    <Check size={13} strokeWidth={3} className="text-white" />
                  )}
                </div>
                <span>Select All ({availableBooks.length})</span>
              </label>

              <span className="text-gray-300">•</span>

              <span className="text-xs sm:text-sm text-gray-500">
                {selectedBooks.length} of {items.length} selected
              </span>
            </div>

            <div className="flex items-center gap-4">
              {selectedBooks.length > 0 && (
                <button
                  onClick={() => setSelectedBooks([])}
                  className="text-xs sm:text-sm text-red-500 hover:text-red-700 font-medium hover:underline transition-colors"
                >
                  Clear Selection
                </button>
              )}

              <Link
                to="/explore-books"
                className="text-xs sm:text-sm font-medium text-primary hover:text-primary-shade4 inline-flex items-center gap-1 group"
              >
                <span>Add More Books</span>
                <span className="group-hover:translate-x-0.5 transition-transform">
                  &rarr;
                </span>
              </Link>
            </div>
          </div>

          {/* Main Layout: Items list on left, Rental Summary on right */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-start">
            {/* Left 2 Columns: Cart Item Cards */}
            <div className="lg:col-span-2 space-y-3 sm:space-y-4">
              {items.map((bookWrapper) => {
                const book = bookWrapper.book;
                if (!book) return null;

                return (
                  <CartItem
                    key={book.bookId}
                    book={{
                      id: book.bookId,
                      title: book.bookName,
                      author: book.author?.authorName || "Unknown Author",
                      publisher: book.bookPublication,
                      isbn: book.bookIsbn13 || book.bookIsbn10,
                      imageUrl: book.bookImages,
                      rating: book.averageRating || 0,
                      stock: book.bookStockStatus,
                    }}
                    isSelected={selectedBooks.includes(book.bookId)}
                    onSelect={() => handleSelect(book)}
                    onRemove={() => handleRemove(book.bookId)}
                  />
                );
              })}
            </div>

            {/* Right 1 Column: Rental Summary Card */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-white rounded-2xl border border-gray-200/80 p-5 sm:p-6 shadow-sm space-y-5">
                <h3 className="font-semibold text-gray-900 text-lg border-b border-gray-100 pb-3">
                  Rental Summary
                </h3>

                <div className="space-y-3 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Total Books in Cart</span>
                    <span className="font-semibold text-gray-900">
                      {items.length}
                    </span>
                  </div>

                  <div className="flex justify-between text-gray-600">
                    <span>Selected for Rent</span>
                    <span className="font-semibold text-primary">
                      {selectedBooks.length}{" "}
                      {selectedBooks.length === 1 ? "book" : "books"}
                    </span>
                  </div>

                  <div className="flex justify-between text-gray-600">
                    <span>Doorstep Delivery & Pickup</span>
                    <span className="font-medium text-emerald-600">Free</span>
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-4">
                  <button
                    onClick={handleProceedToRent}
                    disabled={selectedBooks.length === 0}
                    className={`w-full py-3 px-4 rounded-xl font-semibold text-white shadow-md transition-all flex items-center justify-center gap-2 ${
                      selectedBooks.length === 0
                        ? "bg-gray-300 cursor-not-allowed text-gray-500 shadow-none"
                        : "bg-primary hover:bg-primary-shade4 hover:shadow-lg active:scale-[0.99]"
                    }`}
                  >
                    <span>
                      Proceed to Rent ({selectedBooks.length}{" "}
                      {selectedBooks.length === 1 ? "book" : "books"})
                    </span>
                    <span>&rarr;</span>
                  </button>

                  {selectedBooks.length === 0 && (
                    <p className="text-xs text-amber-600 text-center mt-2.5">
                      Please select at least 1 in-stock book to proceed.
                    </p>
                  )}
                </div>

                <div className="border-t border-gray-100 pt-4">
                  <Link
                    to="/explore-books"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-primary/30 text-primary font-medium text-sm hover:bg-orange-50 hover:border-primary transition"
                  >
                    <ArrowLeft size={16} />
                    <span>Add More Books</span>
                  </Link>
                </div>

                <p className="text-xs text-gray-400 text-center leading-relaxed">
                  Free delivery and pickup at your doorstep. Return or exchange
                  anytime under your active plan.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Limit Exceeded Modal */}
      {showLimitExceededModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full">
            <h2 className="text-lg font-semibold mb-2 text-red-600">
              Limit Reached
            </h2>
            <p className="text-gray-700 mb-3 text-sm leading-relaxed">
              You’ve selected more books than allowed in your current plan. You
              can rent only <strong>{limitExceededCount}</strong> more book
              {limitExceededCount > 1 ? "s" : ""}.
            </p>
            <p className="text-xs text-gray-500 mb-6 leading-relaxed">
              We know you love reading — upgrade your plan to unlock more books
              and never run out of stories!
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowLimitExceededModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <Link
                to="/membershipPlans"
                className="px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary-shade4 transition"
                onClick={() => setShowLimitExceededModal(false)}
              >
                Upgrade Plan
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full">
            <h2 className="text-lg font-semibold mb-2 text-red-600">
              No Active Subscription
            </h2>
            <p className="text-gray-700 mb-3 text-sm leading-relaxed">
              Looks like you don’t have an active subscription. You need an active plan
              to rent books.
            </p>
            <p className="text-xs text-gray-500 mb-6 leading-relaxed">
              Unlock the world of stories – choose a plan that fits your reading appetite!
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowSubscriptionModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <Link
                to="/membershipPlans"
                className="px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary-shade4 transition"
                onClick={() => setShowSubscriptionModal(false)}
              >
                View Plans
              </Link>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
